/**
 * Consent Context
 *
 * Provides cookie consent state and management throughout the application.
 * Handles localStorage persistence, GPC (Global Privacy Control) compliance,
 * and consent versioning for re-prompting when policy changes.
 *
 * Business Rules:
 * - Essential cookies are always enabled (cannot be disabled)
 * - Analytics and Marketing default to false (opt-in required)
 * - GPC (navigator.globalPrivacyControl) forces marketing = false
 * - Consent version mismatch triggers re-prompt (shows banner)
 * - "Do Not Sell or Share" link opens modal with marketing forced off
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useLayoutEffect,
} from 'react';
import type { ReactNode } from 'react';
import type { ConsentState, StoredConsent, ConsentContextValue } from '../types/consent';
import {
  CONSENT_STORAGE_KEY,
  CURRENT_CONSENT_VERSION,
  DEFAULT_CONSENT,
} from '../constants/consent';
import { updateConsentSignals } from '../services/marketingTracking';
import { flushCookieConsentToServer } from '../services/flushCookieConsent';
import { ConsentContext, ConsentModalContext } from './ConsentContext';
import { detectGpc } from '../utils/detectGpc';

/**
 * Read stored consent from localStorage.
 * Returns null if not found, invalid, or version mismatch.
 */
function readStoredConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredConsent;

    // Validate shape
    if (
      typeof parsed !== 'object' ||
      typeof parsed.version !== 'number' ||
      typeof parsed.updatedAtIso !== 'string' ||
      typeof parsed.consent !== 'object' ||
      typeof parsed.consent.analytics !== 'boolean' ||
      typeof parsed.consent.marketing !== 'boolean'
    ) {
      return null;
    }

    // Check version - if mismatch, treat as not set (re-prompt)
    if (parsed.version !== CURRENT_CONSENT_VERSION) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Write consent to localStorage with version and timestamp.
 */
function writeStoredConsent(consent: ConsentState): void {
  const stored: StoredConsent = {
    version: CURRENT_CONSENT_VERSION,
    updatedAtIso: new Date().toISOString(),
    consent,
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
}

/**
 * Props for ConsentProvider.
 */
interface ConsentProviderProps {
  children: ReactNode;
}

/**
 * Internal state for the provider.
 */
interface ConsentProviderState {
  consent: ConsentState;
  isConsentSet: boolean;
  preferencesOpen: boolean;
  doNotSellMode: boolean;
}

/**
 * ConsentProvider component.
 * Wraps the app to provide consent state via context.
 */
export const ConsentProvider: React.FC<ConsentProviderProps> = ({ children }) => {
  // Detect GPC once on mount
  const [isGpcEnabled] = useState(() => detectGpc());

  // Initialize state from localStorage
  const [state, setState] = useState<ConsentProviderState>(() => {
    const stored = readStoredConsent();
    if (stored) {
      return {
        consent: {
          ...stored.consent,
          essential: true, // Always enforce essential = true
        },
        isConsentSet: true,
        preferencesOpen: false,
        doNotSellMode: false,
      };
    }
    return {
      consent: DEFAULT_CONSENT,
      isConsentSet: false,
      preferencesOpen: false,
      doNotSellMode: false,
    };
  });

  /**
   * Compute effective consent (respecting GPC override).
   * If GPC is enabled, marketing is forced to false regardless of stored value.
   */
  const effectiveConsent = useMemo<ConsentState>(() => {
    if (isGpcEnabled) {
      return {
        ...state.consent,
        marketing: false,
      };
    }
    return state.consent;
  }, [state.consent, isGpcEnabled]);

  /**
   * Restore Consent Mode v2 signals from localStorage before child effects
   * (e.g. subscription fetch → free_signup) can fire conversion tags.
   */
  useLayoutEffect(() => {
    if (state.isConsentSet) {
      updateConsentSignals(effectiveConsent);
    }
    // Bootstrap only — setConsent and App.tsx handle later changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Update consent preferences.
   * Writes to localStorage and updates state.
   */
  const setConsent = useCallback((newConsent: Omit<ConsentState, 'essential'>) => {
    const fullConsent: ConsentState = {
      essential: true,
      analytics: newConsent.analytics,
      marketing: newConsent.marketing,
    };

    writeStoredConsent(fullConsent);

    const signalsConsent: ConsentState = isGpcEnabled
      ? { ...fullConsent, marketing: false }
      : fullConsent;

    // Sync immediately so conversion tags on the same interaction are not
    // blocked by default-denied Consent Mode while React re-renders.
    updateConsentSignals(signalsConsent);

    // Best-effort server ledger for Admin Ads reconciliation (PBI #1154).
    void flushCookieConsentToServer({
      analytics: signalsConsent.analytics,
      marketing: signalsConsent.marketing,
    });

    setState((prev) => ({
      ...prev,
      consent: fullConsent,
      isConsentSet: true,
      preferencesOpen: false,
      doNotSellMode: false,
    }));
  }, [isGpcEnabled]);

  /**
   * Open the cookie preferences modal.
   */
  const openPreferences = useCallback(() => {
    setState((prev) => ({
      ...prev,
      preferencesOpen: true,
      doNotSellMode: false,
    }));
  }, []);

  /**
   * Open the preferences modal with marketing forced off (CCPA "Do Not Sell").
   */
  const openDoNotSell = useCallback(() => {
    setState((prev) => ({
      ...prev,
      preferencesOpen: true,
      doNotSellMode: true,
    }));
  }, []);

  // Context value
  const contextValue = useMemo<ConsentContextValue>(
    () => ({
      consent: effectiveConsent,
      isConsentSet: state.isConsentSet,
      isGpcEnabled,
      setConsent,
      openPreferences,
      openDoNotSell,
    }),
    [effectiveConsent, state.isConsentSet, isGpcEnabled, setConsent, openPreferences, openDoNotSell]
  );

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
      {/* Banner and Modal are rendered by consuming components */}
    </ConsentContext.Provider>
  );
};

/**
 * Provider that also exposes modal state for the CookiePreferencesModal.
 * This is the full provider that should be used in App.tsx.
 */
export const ConsentProviderWithModal: React.FC<ConsentProviderProps> = ({ children }) => {
  // Detect GPC once on mount
  const [isGpcEnabled] = useState(() => detectGpc());

  // Initialize state from localStorage
  const [state, setState] = useState<ConsentProviderState>(() => {
    const stored = readStoredConsent();
    if (stored) {
      return {
        consent: {
          ...stored.consent,
          essential: true,
        },
        isConsentSet: true,
        preferencesOpen: false,
        doNotSellMode: false,
      };
    }
    return {
      consent: DEFAULT_CONSENT,
      isConsentSet: false,
      preferencesOpen: false,
      doNotSellMode: false,
    };
  });

  const effectiveConsent = useMemo<ConsentState>(() => {
    if (isGpcEnabled) {
      return {
        ...state.consent,
        marketing: false,
      };
    }
    return state.consent;
  }, [state.consent, isGpcEnabled]);

  /**
   * Restore Consent Mode v2 signals from localStorage before child effects
   * (e.g. subscription fetch → free_signup) can fire conversion tags.
   */
  useLayoutEffect(() => {
    if (state.isConsentSet) {
      updateConsentSignals(effectiveConsent);
    }
    // Bootstrap only — setConsent and App.tsx handle later changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setConsent = useCallback((newConsent: Omit<ConsentState, 'essential'>) => {
    const fullConsent: ConsentState = {
      essential: true,
      analytics: newConsent.analytics,
      marketing: newConsent.marketing,
    };

    writeStoredConsent(fullConsent);

    const signalsConsent: ConsentState = isGpcEnabled
      ? { ...fullConsent, marketing: false }
      : fullConsent;

    updateConsentSignals(signalsConsent);

    // Best-effort server ledger for Admin Ads reconciliation (PBI #1154).
    void flushCookieConsentToServer({
      analytics: signalsConsent.analytics,
      marketing: signalsConsent.marketing,
    });

    setState((prev) => ({
      ...prev,
      consent: fullConsent,
      isConsentSet: true,
      preferencesOpen: false,
      doNotSellMode: false,
    }));
  }, [isGpcEnabled]);

  const openPreferences = useCallback(() => {
    setState((prev) => ({
      ...prev,
      preferencesOpen: true,
      doNotSellMode: false,
    }));
  }, []);

  const openDoNotSell = useCallback(() => {
    setState((prev) => ({
      ...prev,
      preferencesOpen: true,
      doNotSellMode: true,
    }));
  }, []);

  const closePreferences = useCallback(() => {
    setState((prev) => ({
      ...prev,
      preferencesOpen: false,
      doNotSellMode: false,
    }));
  }, []);

  const contextValue = useMemo<ConsentContextValue>(
    () => ({
      consent: effectiveConsent,
      isConsentSet: state.isConsentSet,
      isGpcEnabled,
      setConsent,
      openPreferences,
      openDoNotSell,
    }),
    [effectiveConsent, state.isConsentSet, isGpcEnabled, setConsent, openPreferences, openDoNotSell]
  );

  const modalContextValue = useMemo(
    () => ({
      isOpen: state.preferencesOpen,
      doNotSellMode: state.doNotSellMode,
      close: closePreferences,
    }),
    [state.preferencesOpen, state.doNotSellMode, closePreferences]
  );

  return (
    <ConsentContext.Provider value={contextValue}>
      <ConsentModalContext.Provider value={modalContextValue}>
        {children}
      </ConsentModalContext.Provider>
    </ConsentContext.Provider>
  );
};
